import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { API } from '../../utils/constants';

const CalendarPreviewComponent = ({ 
  calendarJobs, 
  calendarEvents, 
  employees, 
  newJobStartDate, 
  newJobEndDate, 
  newJobCustomer,
  isEditMode = false
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hasUserNavigated, setHasUserNavigated] = useState(false);
  // Mirror main calendar: projected dates for queued jobs without explicit dates
  const [projectedJobDates, setProjectedJobDates] = useState({});

  // Handle newJobStartDate prop changes - but only if user hasn't manually navigated
  React.useEffect(() => {
    if (newJobStartDate && !hasUserNavigated) {
      const [year, month, day] = newJobStartDate.split('-').map(num => parseInt(num));
      const jobDate = new Date(year, month - 1, day); // month is 0-indexed
      setCurrentDate(jobDate);
    }
  }, [newJobStartDate, hasUserNavigated]);

  // Production debug logging
  React.useEffect(() => {
    console.log(`📅 [PRODUCTION DEBUG] Calendar Preview Component loaded:`, {
      isEditMode,
      jobsReceived: calendarJobs?.length || 0,
      eventsReceived: calendarEvents?.length || 0
    });
  }, [calendarJobs, calendarEvents, isEditMode]);

  // Calendar helper functions
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Simple projection of rough dates for jobs based on queue and estimated_duration when start/end not set,
  // mirroring the main calendar logic but scoped locally to preview
  React.useEffect(() => {
    if (!calendarJobs || calendarJobs.length === 0) {
      setProjectedJobDates({});
      return;
    }

    const today = new Date();

    const parseDurationToDays = (durationStr) => {
      if (!durationStr) return 1;
      const lower = durationStr.toLowerCase().trim();
      const dayMatch = lower.match(/(\d+)(\s*)day/);
      if (dayMatch) return Math.max(1, parseInt(dayMatch[1], 10));
      const hourMatch = lower.match(/(\d+)(\s*)hour/);
      if (hourMatch) {
        const hours = parseInt(hourMatch[1], 10) || 0;
        return Math.max(1, Math.ceil(hours / 8));
      }
      return 1;
    };

    const queuedJobs = calendarJobs.filter(
      (job) => !job.start_date && !job.end_date && typeof job.queue_number === 'number'
    );

    queuedJobs.sort((a, b) => (a.queue_number || 0) - (b.queue_number || 0));

    let cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const projected = {};

    queuedJobs.forEach((job) => {
      const jobDays = parseDurationToDays(job.estimated_duration);

      // Skip forward to next weekday if cursor is Sat/Sun
      while (cursor.getDay() === 0 || cursor.getDay() === 6) {
        cursor.setDate(cursor.getDate() + 1);
      }

      const start = new Date(cursor);
      let remainingDays = jobDays;
      let end = new Date(start);

      while (remainingDays > 0) {
        if (end.getDay() !== 0 && end.getDay() !== 6) {
          remainingDays -= 1;
        }
        if (remainingDays > 0) {
          end.setDate(end.getDate() + 1);
        }
      }

      const startStr = formatDate(start);
      const endStr = formatDate(end);
      projected[job.id] = { start_date: startStr, end_date: endStr };

      cursor = new Date(end);
      cursor.setDate(cursor.getDate() + 1);
    });

    setProjectedJobDates(projected);
  }, [calendarJobs]);

  const getJobsForDate = (date) => {
    const dateStr = formatDate(date);
    const jobs = calendarJobs.filter(job => {
      const start = job.start_date || projectedJobDates[job.id]?.start_date;
      const end = job.end_date || projectedJobDates[job.id]?.end_date;
      if (!start || !end) return false;
      return dateStr >= start && dateStr <= end;
    });
    
    return jobs;
  };

  const getEventsForDate = (date) => {
    const dateStr = formatDate(date);
    return calendarEvents.filter(event => {
      if (!event.start_date || !event.end_date) return false;
      return dateStr >= event.start_date && dateStr <= event.end_date;
    });
  };

  // Preview calendar job color by division only (simple, explicit mapping)
  const getJobColor = (job) => {
    if (job.division === 'Foundations') {
      // Foundations = red
      return 'bg-red-500 text-white';
    }
    if (job.division === 'Waterproofing') {
      // Waterproofing = blue
      return 'bg-blue-500 text-white';
    }
    if (job.division === 'Septics') {
      // Septics = yellow
      return 'bg-yellow-300 text-gray-900';
    }

    // Default color if no division set yet
    return 'bg-blue-200 text-blue-900';
  };

  const isNewJobDate = (date) => {
    if (!newJobStartDate || !newJobEndDate) return false;
    const dateStr = formatDate(date);
    return dateStr >= newJobStartDate && dateStr <= newJobEndDate;
  };

  // Update currentDate when navigation occurs - WORKING VERSION, NO DATA FETCHING
  const changeMonth = (direction) => {
    setHasUserNavigated(true); // Prevent useEffect from resetting date
    
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1);
    console.log(`📅 [NAVIGATION] User navigating from ${currentDate.getFullYear()}/${currentDate.getMonth() + 1} to ${newDate.getFullYear()}/${newDate.getMonth() + 1}`);
    setCurrentDate(newDate);
    
    // NO DATA FETCHING - this was the working version
  };

  // Force re-render when currentDate changes
  React.useEffect(() => {
    console.log(`📅 [PRODUCTION DEBUG] Current date changed to: ${currentDate.getFullYear()}/${currentDate.getMonth() + 1}`);
  }, [currentDate]);

  const generateCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const jobs = getJobsForDate(date);
      const events = getEventsForDate(date);
      const isToday = formatDate(date) === formatDate(new Date());
      const isNewJob = isNewJobDate(date);

      days.push(
        <div 
          key={day} 
          className={`calendar-preview-day ${isToday ? 'today' : ''} ${isNewJob ? 'new-job-highlight' : ''}`}
        >
          <div className="day-number">{day}</div>
          
          {isNewJob && (
            <div className="new-job-indicator">
              <div className="bg-green-500 text-white text-xs px-1 py-0.5 rounded font-bold">
                {isEditMode ? 'EDITING' : 'NEW'}: {newJobCustomer}
              </div>
            </div>
          )}
          
          <div className="day-content">
            {/* Existing Jobs */}
            {jobs.map((job, index) => (
              <div
                key={job.id}
                className={`job-preview ${getJobColor(job)} text-xs p-1 rounded mb-1`}
                title={`${job.customer_name} - ${job.job_summary || 'No summary'}`}
              >
                <div className="truncate font-semibold" style={{ fontSize: '9px' }}>
                  {job.customer_name}
                </div>
              </div>
            ))}
            
            {/* Existing Events */}
            {events.map((event, index) => (
              <div
                key={event.id}
                className="event-preview text-xs p-1 rounded mb-1 border-l-2"
                style={{ 
                  backgroundColor: event.color + '20',
                  borderLeftColor: event.color,
                  color: '#374151'
                }}
                title={`${event.title} - ${event.description || ''}`}
              >
                <div className="truncate font-semibold" style={{ fontSize: '9px' }}>
                  {event.title}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return days;
  };

  return (
    <div className="calendar-preview-container">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => changeMonth(-1)}
          className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg font-medium transition-colors"
        >
          ← Previous
        </button>
        <h3 className="text-xl font-bold text-gray-800">
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>
        <button
          onClick={() => changeMonth(1)}
          className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Next →
        </button>
      </div>

      {/* Calendar Legend */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span>{isEditMode ? 'Job Being Edited' : 'Your New Job'}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span>Scheduled Jobs</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-500 border border-dashed border-orange-700 rounded"></div>
            <span>Unassigned Jobs</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-purple-200 border-l-2 border-purple-500 rounded"></div>
            <span>Events</span>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-preview-grid">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center font-bold text-gray-600 p-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {generateCalendar()}
        </div>
      </div>

      {/* Schedule Summary */}
      {newJobStartDate && newJobEndDate && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-bold text-green-800 mb-2">📅 Job Schedule</h4>
          <p className="text-green-700 text-sm">
            <strong>{newJobCustomer}</strong> {isEditMode ? 'is' : 'will be'} scheduled from{' '}
            <strong>{newJobStartDate}</strong> to{' '}
            <strong>{newJobEndDate}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

// Employees Component

const CalendarModule = ({ onBack, currentUser, onEditJob, initialDate }) => {
  const [currentDate, setCurrentDate] = useState(() => initialDate || new Date());
  const [calendarJobs, setCalendarJobs] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showCrewModal, setShowCrewModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDayView, setShowDayView] = useState(false);
  // Employee filtering
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  // Available crew functionality
  const [showAvailableCrewModal, setShowAvailableCrewModal] = useState(false);
  const [selectedDateForCrew, setSelectedDateForCrew] = useState(null);
  const [availableCrewForDate, setAvailableCrewForDate] = useState([]);
  
  // Projected dates for queued jobs without explicit dates
  const [projectedJobDates, setProjectedJobDates] = useState({});
  
  // Crew Assignment Mode
  const [isCrewAssignmentMode, setIsCrewAssignmentMode] = useState(false);
  const [showCrewAssignmentView, setShowCrewAssignmentView] = useState(false);
  const [crewAssignmentDate, setCrewAssignmentDate] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [crewAssignments, setCrewAssignments] = useState({});
  const [unassignedEmployees, setUnassignedEmployees] = useState([]);
  const [savingAssignments, setSavingAssignments] = useState(false);

  // Mobile detection effect
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize calendar
  React.useEffect(() => {
    fetchEmployees();
    fetchCalendarJobs();
    fetchCalendarEvents();
    fetchMonthlyRevenue();
  }, [currentDate]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees/active`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchCalendarJobs = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API}/calendar/jobs/${year}/${month}`);
      setCalendarJobs(response.data);
    } catch (error) {
      console.error('Error fetching calendar jobs:', error);
      setCalendarJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyRevenue = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API}/calendar/revenue/${year}/${month}`);
      setRevenue(response.data);
    } catch (error) {
      console.error('Error fetching monthly revenue:', error);
      setRevenue(null);
    }
  };

  const fetchCalendarEvents = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API}/calendar/events/${year}/${month}`);
      setCalendarEvents(response.data);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
  // Simple projection of rough dates for jobs based on queue and estimated_duration when start/end not set
  React.useEffect(() => {
    // Only project for current month view
    if (!calendarJobs || calendarJobs.length === 0) {
      setProjectedJobDates({});
      return;
    }

    const workingDaysPerWeek = 5; // Mon–Fri
    const today = new Date();

    // Helper to parse a human duration like "2 days" or "4 hours" into day units (very approximate)
    const parseDurationToDays = (durationStr) => {
      if (!durationStr) return 1;
      const lower = durationStr.toLowerCase().trim();
      const dayMatch = lower.match(/(\d+)(\s*)day/);
      if (dayMatch) return Math.max(1, parseInt(dayMatch[1], 10));
      const hourMatch = lower.match(/(\d+)(\s*)hour/);
      if (hourMatch) {
        const hours = parseInt(hourMatch[1], 10) || 0;
        return Math.max(1, Math.ceil(hours / 8));
      }
      return 1;
    };

    // Separate jobs with explicit dates vs queued-only jobs
    const datedJobs = calendarJobs.filter(
      (job) => job.start_date && job.end_date
    );
    const queuedJobs = calendarJobs.filter(
      (job) => !job.start_date && !job.end_date && typeof job.queue_number === 'number'
    );

    // Sort queued jobs by queue_number ascending
    queuedJobs.sort((a, b) => (a.queue_number || 0) - (b.queue_number || 0));

    // Start projection from today + a small offset (e.g. 1 day)
    let cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

    const projected = {};

    queuedJobs.forEach((job) => {
      const jobDays = parseDurationToDays(job.estimated_duration);
      
      // Skip forward to next weekday if cursor is Sat/Sun
      while (cursor.getDay() === 0 || cursor.getDay() === 6) {
        cursor.setDate(cursor.getDate() + 1);
      }

      const start = new Date(cursor);
      let remainingDays = jobDays;
      let end = new Date(start);

      // Walk across calendar, skipping weekends, to cover jobDays
      while (remainingDays > 0) {
        if (end.getDay() !== 0 && end.getDay() !== 6) {
          remainingDays -= 1;
        }
        if (remainingDays > 0) {
          end.setDate(end.getDate() + 1);
        }
      }

      // Save projection as ISO strings
      const startStr = formatDate(start);
      const endStr = formatDate(end);
      projected[job.id] = { start_date: startStr, end_date: endStr };

      // Move cursor to the day after this job ends
      cursor = new Date(end);
      cursor.setDate(cursor.getDate() + 1);
    });

    setProjectedJobDates(projected);
  }, [calendarJobs]);


      setCalendarEvents([]);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const parseDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00');
  };

  // Get jobs for a specific date
  const getFilteredCalendarJobs = () => {
    if (selectedEmployee === 'all') {
      return calendarJobs;
    }
    
    return calendarJobs.filter(job => {
      return job.assigned_crew_members && job.assigned_crew_members.includes(selectedEmployee);
    });
  };

  const getJobsForDate = (date) => {
    // Skip weekends for job bars (Mon–Fri only)
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return [];
    }

    const dateStr = formatDate(date);
    let filteredJobs = calendarJobs.filter((job) => {
      const start = job.start_date || projectedJobDates[job.id]?.start_date;
      const end = job.end_date || projectedJobDates[job.id]?.end_date;
      if (!start || !end) return false;
      return dateStr >= start && dateStr <= end;
    });

    // Apply employee filter if not showing all employees
    if (selectedEmployee !== 'all') {
      filteredJobs = filteredJobs.filter(job => {
        return job.assigned_crew_members && job.assigned_crew_members.includes(selectedEmployee);
      });
    }

    return filteredJobs;
  };

  // Get available crew members for a specific date
  const getAvailableCrewForDate = (date) => {
    const dateStr = formatDate(date);
    const jobsOnDate = calendarJobs.filter(job => {
      if (!job.start_date || !job.end_date) return false;
      return dateStr >= job.start_date && dateStr <= job.end_date;
    });

    // Get all assigned crew members for this date
    const assignedCrewIds = new Set();
    jobsOnDate.forEach(job => {
      if (job.assigned_crew_members && job.assigned_crew_members.length > 0) {
        job.assigned_crew_members.forEach(crewId => {
          assignedCrewIds.add(crewId);
        });
      }
    });

    // Filter out assigned crew to get available crew
    const availableCrew = employees.filter(employee => 
      employee.active && !assignedCrewIds.has(employee.id)
    );

    return availableCrew;
  };

  // Handle clicking on available crew count
  const handleAvailableCrewClick = (date, e) => {
    e.stopPropagation(); // Prevent calendar day click
    const availableCrew = getAvailableCrewForDate(date);
    setSelectedDateForCrew(date);
    setAvailableCrewForDate(availableCrew);
    setShowAvailableCrewModal(true);
  };

  // Job color helpers - Color by crew lead instead of location
  const getCrewLeadColors = () => {
    return [
      'bg-blue-500 text-white',      // Crew Lead 1
      'bg-green-500 text-white',     // Crew Lead 2  
      'bg-purple-500 text-white',    // Crew Lead 3
      'bg-red-500 text-white',       // Crew Lead 4
      'bg-yellow-500 text-black',    // Crew Lead 5
      'bg-indigo-500 text-white',    // Crew Lead 6
      'bg-pink-500 text-white',      // Crew Lead 7
      'bg-teal-500 text-white',      // Crew Lead 8
      'bg-orange-500 text-white',    // Crew Lead 9
      'bg-cyan-500 text-white'       // Crew Lead 10
    ];
  };

  const getCrewLeadName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
  };

  const getJobColor = (job) => {
    // Color strictly by division for clarity
    if (job.division === 'Foundations') {
      // Foundations = red
      return 'bg-red-500 text-white';
    }
    if (job.division === 'Waterproofing') {
      // Waterproofing = blue
      return 'bg-blue-500 text-white';
    }
    if (job.division === 'Septics') {
      // Septics = yellow
      return 'bg-yellow-300 text-gray-900';
    }

    // If no division, keep a neutral default
    return 'bg-blue-200 text-blue-900';
  };

  const getUniqueCrewLeads = () => {
    const crewLeads = new Set();
    const colors = getCrewLeadColors();
    
    calendarJobs.forEach(job => {
      if (job.assigned_crew_members && job.assigned_crew_members.length > 0) {
        const crewLeadId = job.assigned_crew_members[0];
        const crewLead = employees.find(emp => emp.id === crewLeadId);
        if (crewLead) {
          crewLeads.add({
            id: crewLeadId,
            name: `${crewLead.first_name} ${crewLead.last_name}`,
            color: colors[employees.findIndex(emp => emp.id === crewLeadId) % colors.length]
          });
        }
      }
    });
    
    return Array.from(crewLeads);
  };

  // Navigation functions
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Job management functions
  const handleDateClick = (date) => {
    if (isCrewAssignmentMode) {
      handleDayClickForCrewAssignment(date);
    } else {
      setSelectedDate(date);
      setShowDayView(true);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobModal(true);
  };

  const handleCrewAssignment = (job) => {
    setSelectedJob(job);
    setShowCrewModal(true);
  };

  // Calendar Events functions
  const getEventsForDate = (date) => {
    const dateStr = formatDate(date);
    return calendarEvents.filter(event => {
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      const currentDate = new Date(dateStr);
      
      return currentDate >= startDate && currentDate <= endDate;
    });
  };

  const handleCreateEvent = async (eventData) => {
    try {
      await axios.post(`${API}/calendar/events`, {
        ...eventData,
        created_by: currentUser.email
      });
      
      // Refresh calendar events
      fetchCalendarEvents();
      setShowNewEventModal(false);
      alert('Event created successfully!');
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Error creating event. Please try again.');
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleEditEvent = async (eventId, eventData) => {
    try {
      await axios.put(`${API}/calendar/events/${eventId}`, eventData);
      
      // Refresh calendar events
      fetchCalendarEvents();
      setShowEventModal(false);
      setSelectedEvent(null);
      alert('Event updated successfully!');
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Error updating event. Please try again.');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      try {
        await axios.delete(`${API}/calendar/events/${eventId}`);
        
        // Refresh calendar events
        fetchCalendarEvents();
        setShowEventModal(false);
        setSelectedEvent(null);
        alert('Event deleted successfully!');
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Error deleting event. Please try again.');
      }
    }
  };

  // ==================== CREW ASSIGNMENT FUNCTIONS ====================
  
  const handleDayClickForCrewAssignment = async (date) => {
    setCrewAssignmentDate(date);
    setShowCrewAssignmentView(true);
    await fetchCrewAssignmentData(date);
  };
  
  const fetchCrewAssignmentData = async (date) => {
    try {
      // Fetch active jobs
      const jobsResponse = await axios.get(`${API}/jobs/active`);
      setActiveJobs(jobsResponse.data || []);
      
      // Fetch existing assignments for selected date
      const dateStr = formatDate(date);
      const assignmentsResponse = await axios.get(`${API}/crew-assignments/${dateStr}`);
      
      // Build assignments object from response
      const assignmentsMap = {};
      (assignmentsResponse.data || []).forEach(assignment => {
        assignmentsMap[assignment.job_id] = assignment.employee_ids || [];
      });
      setCrewAssignments(assignmentsMap);
      
      // Calculate unassigned employees
      updateUnassignedEmployeesForAssignment(assignmentsMap, employees);
      
    } catch (error) {
      console.error('Error fetching crew assignment data:', error);
      alert('Error loading crew assignment data. Please try again.');
    }
  };
  
  const updateUnassignedEmployeesForAssignment = (assignmentsMap, allEmployees) => {
    const assignedIds = new Set();
    Object.values(assignmentsMap).forEach(empIds => {
      empIds.forEach(id => assignedIds.add(id));
    });
    
    const unassigned = allEmployees.filter(emp => !assignedIds.has(emp.id));
    setUnassignedEmployees(unassigned);
  };
  
  const onCrewDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    
    const employeeId = draggableId;
    const newAssignments = { ...crewAssignments };
    
    // Remove from source
    if (source.droppableId !== 'unassigned') {
      const sourceJobId = source.droppableId;
      newAssignments[sourceJobId] = (newAssignments[sourceJobId] || []).filter(id => id !== employeeId);
    }
    
    // Add to destination
    if (destination.droppableId !== 'unassigned') {
      const destJobId = destination.droppableId;
      if (!newAssignments[destJobId]) {
        newAssignments[destJobId] = [];
      }
      if (!newAssignments[destJobId].includes(employeeId)) {
        newAssignments[destJobId].push(employeeId);
      }
    }
    
    setCrewAssignments(newAssignments);
    updateUnassignedEmployeesForAssignment(newAssignments, employees);
  };
  
  const handleSaveCrewAssignments = async () => {
    setSavingAssignments(true);
    try {
      const dateStr = formatDate(crewAssignmentDate);
      
      const assignmentsData = Object.keys(crewAssignments).map(jobId => ({
        job_id: jobId,
        employee_ids: crewAssignments[jobId] || []
      }));
      
      await axios.post(`${API}/crew-assignments`, {
        date: dateStr,
        assignments: assignmentsData
      });
      
      alert('✅ Crew assignments saved successfully!');
      setShowCrewAssignmentView(false);
    } catch (error) {
      console.error('Error saving assignments:', error);
      alert('❌ Error saving assignments. Please try again.');
    } finally {
      setSavingAssignments(false);
    }
  };
  
  const getEmployeeNameForAssignment = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
  };

  // Generate calendar grid
  const generateCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const jobs = getJobsForDate(date);
      const events = getEventsForDate(date);

      // Determine if this is the first day a job appears in this week (for multi-day bar)
      const isMonday = date.getDay() === 1;
      const isFirstOfMonth = day === 1;

      const isToday = formatDate(date) === formatDate(new Date());

      const maxJobsToShow = 4;
      const jobsToShow = jobs.slice(0, maxJobsToShow);
      const extraJobsCount = jobs.length - jobsToShow.length;

      days.push(
        <div
          key={day}
          className={`relative bg-white min-h-[6rem] sm:min-h-[7.5rem] p-1.5 sm:p-2 hover:bg-slate-50 cursor-pointer flex flex-col ${
            isToday ? 'ring-2 ring-blue-400 ring-offset-1' : ''
          }`}
          style={{ borderTop: '1px solid #E5E7EB', borderLeft: '1px solid #E5E7EB' }}
          onClick={() => handleDateClick(date)}
        >
          {/* Date number */}
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] sm:text-xs font-medium text-gray-500">
              {date.toLocaleDateString('en-US', { weekday: 'short' })}
            </span>
            <span className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full text-xs sm:text-sm font-semibold text-gray-800 bg-gray-100">
              {day}
            </span>
          </div>

          {/* Jobs */}
          <div className="mt-1 flex-1 overflow-hidden space-y-0.5">
            {jobsToShow.map((job) => {
              const colorClass = getJobColor(job);
              return (
                <button
                  key={job.id}
                  type="button"
                  className={`w-full text-left rounded-md border border-slate-200 px-1.5 py-0.5 text-[10px] leading-tight hover:opacity-90 flex items-center gap-1 ${colorClass}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJobClick(job);
                  }}
                  title={`${job.customer_name || ''}`}
                >
                  <span className="inline-flex items-center justify-center rounded-full bg-black/20 text-[9px] font-semibold px-1 min-w-[16px]">
                    {job.queue_number ? `Q${job.queue_number}` : 'Q-'}
                  </span>
                  <span className="truncate flex-1">
                    {job.customer_name || 'Untitled Job'}
                  </span>
                </button>
              );
            })}

            {extraJobsCount > 0 && (
              <div className="text-[10px] text-gray-500">+{extraJobsCount} more</div>
            )}

            {/* Events */}
            {events.map((event) => (
              <button
                key={event.id}
                type="button"
                className="w-full text-left rounded-md border-l-2 px-1.5 py-0.5 text-[10px] leading-tight text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                style={{
                  backgroundColor: event.color + '10',
                  borderLeftColor: event.color,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEventClick(event);
                }}
                title={event.title}
              >
                <span className="text-[10px]">
                  {event.event_type === 'vacation' && '🏖️'}
                  {event.event_type === 'meeting' && '📅'}
                  {event.event_type === 'maintenance' && '🔧'}
                  {event.event_type === 'other' && '📌'}
                </span>
                <span className="truncate flex-1">{event.title}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800">📅 JOB CALENDAR</h1>
          </div>
          <div className="w-16"></div> {/* Spacer for centering */}
        </div>

        {/* Calendar Navigation */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
          {/* Navigation Buttons */}
          <div className="flex items-center justify-center lg:justify-start gap-2 lg:gap-4">
            <button
              onClick={previousMonth}
              className="bg-gray-600 hover:bg-gray-700 px-3 py-2 lg:px-4 lg:py-2 rounded-lg text-white font-semibold text-sm lg:text-base"
            >
              ← Prev
            </button>
            <button
              onClick={goToToday}
              className="bg-blue-600 hover:bg-blue-700 px-3 py-2 lg:px-4 lg:py-2 rounded-lg text-white font-semibold text-sm lg:text-base"
            >
              Today
            </button>
            <button
              onClick={nextMonth}
              className="bg-gray-600 hover:bg-gray-700 px-3 py-2 lg:px-4 lg:py-2 rounded-lg text-white font-semibold text-sm lg:text-base"
            >
              Next →
            </button>
          </div>
          
          {/* Month/Year Header */}
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800 text-center lg:text-left">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>

          {/* Employee Filter and Add Event Button */}
          <div className="flex flex-col sm:flex-row items-center gap-2 lg:gap-4">
            <div className="flex items-center gap-2">
              <label className="text-xs lg:text-sm font-semibold text-gray-700">Filter:</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg px-2 py-1 lg:px-3 lg:py-2 text-xs lg:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-32 lg:max-w-none"
              >
                <option value="all">All Employees</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Add Event Button (Admin Only) */}
            {currentUser?.rawRole === 'admin' && (
              <button
                onClick={() => setShowNewEventModal(true)}
                className="bg-green-600 hover:bg-green-700 px-3 py-2 lg:px-4 lg:py-2 rounded-lg text-white font-semibold text-xs lg:text-sm flex items-center gap-2"
              >
                📅 Add Event
              </button>
            )}
          </div>
        </div>

        {/* Legend removed from calendar header */}
        {/* Calendar Grid */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-gray-900 text-gray-100 text-[11px] sm:text-xs font-semibold uppercase tracking-wide">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="px-2 py-2 sm:px-3 sm:py-3 text-center border-b border-gray-700">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar days */}
          <div className="grid grid-cols-7 bg-gray-200 border-b border-gray-300 border-r border-gray-300">
            {loading ? (
              <div className="col-span-7 text-center py-12 text-gray-500 bg-white">
                Loading calendar...
              </div>
            ) : (
              generateCalendar()
            )}
          </div>
        </div>

        {/* Month overview summary only (job & crew status cards removed) */}
        <div className="mt-6 grid grid-cols-1 gap-4">
          <div className="bg-white rounded-lg p-3 lg:p-4 shadow">
            <h3 className="font-bold text-gray-800 mb-2 text-sm lg:text-base">📊 Month Overview {selectedEmployee !== 'all' ? `(${employees.find(e => e.id === selectedEmployee)?.first_name} ${employees.find(e => e.id === selectedEmployee)?.last_name})` : ''}</h3>
            <p className="text-xs lg:text-sm text-gray-600">Total Jobs: {getFilteredCalendarJobs().length}</p>
            <p className="text-xs lg:text-sm text-gray-600">In Town: {getFilteredCalendarJobs().filter(j => j.location_type === 'in_town').length}</p>
            <p className="text-xs lg:text-sm text-gray-600">Out of Town: {getFilteredCalendarJobs().filter(j => j.location_type === 'out_of_town').length}</p>
          </div>
        </div>

        {/* Quick Actions block removed from calendar footer */}

      </div>
      
      {/* New Job Modal */}
      {showNewJobModal && selectedDate && (
        <CalendarJobModal
          date={selectedDate}
          onClose={() => {
            setShowNewJobModal(false);
            setSelectedDate(null);
          }}
          onSave={() => {
            fetchCalendarJobs();
            setShowNewJobModal(false);
            setSelectedDate(null);
          }}
        />
      )}

      {/* New Event Modal */}
      {showNewEventModal && (
        <NewEventModal
          employees={employees}
          onClose={() => setShowNewEventModal(false)}
          onSave={handleCreateEvent}
        />
      )}

      {/* Event Details/Edit Modal */}
      {showEventModal && selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          employees={employees}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onEdit={handleEditEvent}
          onDelete={handleDeleteEvent}
          currentUser={currentUser}
        />
      )}

      {/* Job Details Modal */}
      {showJobModal && selectedJob && (
        <JobDetailsModal
          job={selectedJob}
          employees={employees}
          currentUser={currentUser}
          onClose={() => {
            setShowJobModal(false);
            setSelectedJob(null);
          }}
          onUpdate={() => {
            fetchCalendarJobs();
            setShowJobModal(false);
            setSelectedJob(null);
          }}
          onEditJob={() => {
            if (onEditJob) {
              onEditJob(selectedJob);
            }
          }}
        />
      )}
      {/* Day View Modal */}
      {showDayView && selectedDate && (
        <DayViewModal
          date={selectedDate}
          jobs={getJobsForDate(selectedDate)}
          events={getEventsForDate(selectedDate)}
          employees={employees}
          currentUser={currentUser}
          onClose={() => {
            setShowDayView(false);
            setSelectedDate(null);
          }}
          onJobClick={(job) => {
            setSelectedJob(job);
            setShowDayView(false);
            setShowJobModal(true);
          }}
          onEventClick={(event) => {
            setSelectedEvent(event);
            setShowDayView(false);
            setShowEventModal(true);
          }}
          onCreateJob={() => {
            setShowDayView(false);
            setShowNewJobModal(true);
          }}
          onCreateEvent={() => {
            setShowDayView(false);
            setShowNewEventModal(true);
          }}
          currentUser={currentUser}
        />
      )}
      
      {/* Available Crew Modal */}
      {showAvailableCrewModal && selectedDateForCrew && (
        <AvailableCrewModal
          date={selectedDateForCrew}
          availableCrew={availableCrewForDate}
          onClose={() => {
            setShowAvailableCrewModal(false);
            setSelectedDateForCrew(null);
            setAvailableCrewForDate([]);
          }}
        />
      )}
      
      {/* Crew Assignment View Modal */}
      {showCrewAssignmentView && crewAssignmentDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-green-600 text-white p-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold">👷 Daily Crew Assignment</h2>
                <p className="text-green-100 text-sm">
                  {crewAssignmentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveCrewAssignments}
                  disabled={savingAssignments}
                  className="bg-white text-green-600 hover:bg-green-50 disabled:bg-gray-300 px-6 py-2 rounded-lg font-semibold"
                >
                  {savingAssignments ? '💾 Saving...' : '💾 Save'}
                </button>
                <button
                  onClick={() => setShowCrewAssignmentView(false)}
                  className="text-green-100 hover:text-white text-3xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content - Side by Side Layout */}
            <div className="flex-1 p-4 overflow-hidden">
              <DragDropContext onDragEnd={onCrewDragEnd}>
                <div className="grid grid-cols-2 gap-4 h-full">
                  
                  {/* Left Column - Unassigned Employees */}
                  <div className="flex flex-col overflow-hidden">
                    <div className="bg-gray-50 rounded-lg p-3 mb-2 flex-shrink-0">
                      <h3 className="text-lg font-bold text-gray-800">
                        🧑‍🤝‍🧑 Unassigned Employees ({unassignedEmployees.length})
                      </h3>
                      <p className="text-gray-600 text-xs">Drag to assign</p>
                    </div>
                    
                    <div className="flex-1 overflow-hidden">
                      <Droppable droppableId="unassigned">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`h-full overflow-y-auto p-3 rounded-lg border-2 border-dashed ${
                              snapshot.isDraggingOver ? 'bg-blue-50 border-blue-400' : 'bg-gray-50 border-gray-300'
                            }`}
                            style={{ maxHeight: '100%' }}
                          >
                            {unassignedEmployees.length === 0 ? (
                              <p className="text-gray-400 text-center py-8 text-sm">All employees assigned!</p>
                            ) : (
                              unassignedEmployees.map((employee, index) => (
                                <Draggable key={employee.id} draggableId={employee.id} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`bg-white p-2 mb-2 rounded-lg shadow-sm border border-gray-200 cursor-move hover:shadow-md transition-shadow ${
                                        snapshot.isDragging ? 'shadow-lg rotate-2 ring-2 ring-blue-400' : ''
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-400 text-sm">⋮⋮</span>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-semibold text-gray-800 text-sm truncate">
                                            {employee.first_name} {employee.last_name}
                                          </div>
                                          <div className="text-xs text-gray-500 truncate">{employee.position}</div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  </div>

                  {/* Right Column - Jobs List */}
                  <div className="flex flex-col overflow-hidden">
                    <div className="bg-gray-50 rounded-lg p-3 mb-2 flex-shrink-0">
                      <h3 className="text-lg font-bold text-gray-800">
                        🏗️ Active Jobs ({activeJobs.length})
                      </h3>
                      <p className="text-gray-600 text-xs">Drop employees here</p>
                    </div>
                    
                    <div className="flex-1 overflow-hidden">
                      {activeJobs.length === 0 ? (
                        <div className="h-full flex items-center justify-center bg-white rounded-lg border border-gray-200">
                          <div className="text-center">
                            <div className="text-5xl mb-2">📋</div>
                            <p className="text-gray-500 text-sm">No active jobs found</p>
                          </div>
                        </div>
                      ) : (
                        <div className="h-full overflow-y-auto space-y-2 pr-2" style={{ maxHeight: '100%' }}>
                          {activeJobs.map(job => (
                            <div key={job.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0 pr-2">
                                  <h4 className="font-bold text-gray-800 text-sm truncate">{job.customer_name}</h4>
                                  <p className="text-xs text-gray-600 truncate">{job.job_location}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                                    {job.job_status}
                                  </span>
                                  <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                                    👷 {(crewAssignments[job.id] || []).length}
                                  </span>
                                </div>
                              </div>
                              
                              <Droppable droppableId={job.id}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`min-h-[60px] p-2 rounded-lg border-2 border-dashed ${
                                      snapshot.isDraggingOver ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-300'
                                    }`}
                                  >
                                    {(!crewAssignments[job.id] || crewAssignments[job.id].length === 0) ? (
                                      <p className="text-gray-400 text-center py-2 text-xs">Drop here</p>
                                    ) : (
                                      (crewAssignments[job.id] || []).map((employeeId, index) => (
                                        <Draggable key={employeeId} draggableId={employeeId} index={index}>
                                          {(provided, snapshot) => (
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              className={`bg-green-100 p-1.5 mb-1 rounded cursor-move hover:bg-green-200 transition-colors ${
                                                snapshot.isDragging ? 'shadow-lg rotate-2 ring-2 ring-green-400' : ''
                                              }`}
                                            >
                                              <div className="flex items-center gap-1">
                                                <span className="text-green-600 text-xs">⋮⋮</span>
                                                <span className="font-medium text-green-800 text-xs truncate">
                                                  {getEmployeeNameForAssignment(employeeId)}
                                                </span>
                                              </div>
                                            </div>
                                          )}
                                        </Draggable>
                                      ))
                                    )}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </DragDropContext>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

// ==================== DAILY CREW ASSIGNMENT MODULE ====================

const DayViewModal = ({ date, jobs, events, employees, onClose, onJobClick, onEventClick, onCreateJob, onCreateEvent, currentUser }) => {
  // Prevent body scroll when modal is open
  React.useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric', 
      month: 'long',
      day: 'numeric'
    });
  };

  const getCrewLeadName = (memberId) => {
    const employee = employees.find(emp => emp.id === memberId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
  };

  const getJobColor = (job) => {
    // Color strictly by division for clarity across both main calendar and day view
    if (job.division === 'Foundations') {
      // Foundations = red
      return 'bg-red-500 text-white';
    }
    if (job.division === 'Waterproofing') {
      // Waterproofing = blue
      return 'bg-blue-500 text-white';
    }
    if (job.division === 'Septics') {
      // Septics = yellow
      return 'bg-yellow-300 text-gray-900';
    }

    // Jobs with Other/blank division show as neutral grey
    return 'bg-gray-300 text-gray-900';
  };

  const totalItems = jobs.length + events.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        
        {/* Header - Fixed */}
        <div className="p-4 sm:p-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl sm:text-2xl font-bold">📅 {formatDate(date)}</h2>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl sm:text-3xl transition-colors p-1"
            >
              ×
            </button>
          </div>
          <p className="text-sm sm:text-base opacity-90">
            {totalItems === 0 ? 'No scheduled items' : 
             totalItems === 1 ? '1 scheduled item' : 
             `${totalItems} scheduled items`}
          </p>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {totalItems === 0 ? (
            /* Empty State */
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">📅</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No items scheduled</h3>
              <p className="text-gray-500 mb-6">Nothing planned for this day yet.</p>
              
              {currentUser?.rawRole === 'admin' && (
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button 
                    onClick={onCreateJob}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    📋 Create Job
                  </button>
                  <button 
                    onClick={onCreateEvent}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    📅 Create Event
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-6">
              
              {/* Jobs Section */}
              {jobs.length > 0 && (
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                      {jobs.length}
                    </span>
                    📋 Jobs Scheduled
                  </h3>
                  
                  <div className="space-y-3">
                    {jobs.map((job) => (
                      <div 
                        key={job.id}
                        onClick={() => onJobClick(job)}
                        className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          
                          {/* Job Color Indicator */}
                          <div className={`w-4 h-4 rounded-full ${getJobColor(job)} flex-shrink-0`}></div>
                          
                          <div className="flex-1 min-w-0">
                            
                            {/* Customer Name and Out of Town Indicator */}
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-lg font-semibold text-gray-900 truncate">
                                {job.customer_name}
                              </h4>
                              {job.location_type === 'out_of_town' && (
                                <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                  ✈️ Out of Town
                                </span>
                              )}
                            </div>
                            
                            {/* Location */}
                            <p className="text-gray-600 text-sm mb-2 truncate">
                              📍 {job.job_location}
                            </p>
                            
                            {/* Job Summary/Details */}
                            {(job.job_summary || job.job_details) && (
                              <p className="text-gray-700 text-sm mb-2 line-clamp-2">
                                {job.job_summary || job.job_details}
                              </p>
                            )}
                            
                            {/* Crew Information */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                              {job.assigned_crew_members && job.assigned_crew_members.length > 0 ? (
                                <>
                                  <div className="flex items-center gap-1 text-sm text-gray-600">
                                    <span>👤</span>
                                    <span className="font-medium">
                                      {getCrewLeadName(job.assigned_crew_members[0])}
                                    </span>
                                    <span className="text-gray-500">(Lead)</span>
                                  </div>
                                  <div className="flex items-center gap-1 text-sm text-gray-600">
                                    <span>👥</span>
                                    <span>{job.assigned_crew_members.length} crew members</span>
                                  </div>
                                </>
                              ) : (
                                <div className="text-sm text-orange-600 font-medium">
                                  ⚠️ No crew assigned
                                </div>
                              )}
                            </div>
                            
                            {/* Additional Info */}
                            <div className="flex flex-wrap gap-2 mt-3">
                              {job.priority_level && job.priority_level !== 'Normal' && (
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  job.priority_level === 'High' ? 'bg-red-100 text-red-800' :
                                  job.priority_level === 'Low' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {job.priority_level} Priority
                                </span>
                              )}
                              {job.job_status && (
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  job.job_status === 'Completed' ? 'bg-green-100 text-green-800' :
                                  job.job_status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                  job.job_status === 'Scheduled' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {job.job_status}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Arrow Indicator */}
                          <div className="text-gray-400 text-xl flex-shrink-0 self-center">
                            →
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Events Section */}
              {events.length > 0 && (
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                      {events.length}
                    </span>
                    🎉 Events Scheduled
                  </h3>
                  
                  <div className="space-y-3">
                    {events.map((event) => (
                      <div 
                        key={event.id}
                        onClick={() => onEventClick(event)}
                        className="bg-white border-l-4 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        style={{ borderLeftColor: event.color }}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          
                          {/* Event Icon */}
                          <div className="text-2xl flex-shrink-0">
                            {event.event_type === 'vacation' && '🏖️'}
                            {event.event_type === 'meeting' && '📅'}
                            {event.event_type === 'maintenance' && '🔧'}
                            {event.event_type === 'other' && '📌'}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            
                            {/* Event Title */}
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">
                              {event.title}
                            </h4>
                            
                            {/* Event Description */}
                            {event.description && (
                              <p className="text-gray-700 text-sm mb-2">
                                {event.description}
                              </p>
                            )}
                            
                            {/* Time and Duration */}
                            <div className="flex flex-col sm:flex-row gap-2 text-sm text-gray-600">
                              {event.all_day ? (
                                <span className="flex items-center gap-1">
                                  🕐 All Day Event
                                </span>
                              ) : (
                                <>
                                  {event.start_time && (
                                    <span className="flex items-center gap-1">
                                      🕐 {event.start_time}
                                    </span>
                                  )}
                                  {event.end_time && event.end_time !== event.start_time && (
                                    <span className="flex items-center gap-1">
                                      - {event.end_time}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            
                            {/* Event Type */}
                            <div className="mt-2">
                              <span className="text-xs px-2 py-1 rounded-full" style={{ 
                                backgroundColor: event.color + '20',
                                color: event.color 
                              }}>
                                {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Arrow Indicator */}
                          <div className="text-gray-400 text-xl flex-shrink-0 self-center">
                            →
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Buttons - Fixed at bottom */}
              {currentUser?.rawRole === 'admin' && (
                <div className="pt-4 border-t border-gray-200 sticky bottom-0 bg-white">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button 
                      onClick={onCreateJob}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      📋 Create Job for This Day
                    </button>
                    <button 
                      onClick={onCreateEvent}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      📅 Create Event for This Day
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
  );
};

// Available Crew Modal Component
const AvailableCrewModal = ({ date, availableCrew, onClose }) => {
  // Prevent body scroll when modal is open
  React.useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric', 
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">👥 Available Crew Members</h2>
            <p className="text-blue-100 text-sm">{formatDate(date)}</p>
          </div>
          <button
            onClick={onClose}
            className="text-blue-100 hover:text-white text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {availableCrew.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">😔</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Available Crew Members</h3>
              <p className="text-gray-600">All crew members are scheduled on jobs for this day.</p>
            </div>
          ) : (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {availableCrew.length} Crew Member{availableCrew.length !== 1 ? 's' : ''} Available
                </h3>
                <p className="text-gray-600 text-sm">
                  These crew members are not scheduled on any jobs for {formatDate(date)}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {availableCrew.map((employee) => (
                  <div key={employee.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                        {employee.first_name.charAt(0)}{employee.last_name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800">
                          {employee.first_name} {employee.last_name}
                        </h4>
                        <div className="text-sm text-gray-600">
                          <p>{employee.position} • {employee.department}</p>
                          {employee.phone && (
                            <p className="text-xs text-gray-500">📞 {employee.phone}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-green-600 font-bold text-lg">
                        ✓
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
        </div>
      </div>
  );
};

// Calendar Job Creation Modal
const CalendarJobModal = ({ date, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    job_location: '',
    contact_email: '',
    contact_phone: '',
    job_details: '',
    job_summary: '',
    start_date: date.toISOString().split('T')[0],
    end_date: date.toISOString().split('T')[0],
    location_type: 'in_town',
    priority_level: 'Normal',
    job_status: 'Scheduled',
    estimated_cost: '',
    billing_status: 'not_billed'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/calendar/jobs`, formData);
      alert('Job created successfully!');
      onSave();
    } catch (error) {
      console.error('Error creating job:', error);
      alert('Error creating job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">
          📅 Create Job for {date.toLocaleDateString()}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Customer Name"
              value={formData.customer_name}
              onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              required
            />
            <input
              type="text"
              placeholder="Job Location"
              value={formData.job_location}
              onChange={(e) => setFormData({...formData, job_location: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="email"
              placeholder="Contact Email"
              value={formData.contact_email}
              onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              required
            />
            <input
              type="tel"
              placeholder="Contact Phone"
              value={formData.contact_phone}
              onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Date Range */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Start Date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Location Type & Priority */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Location Type</label>
              <select
                value={formData.location_type}
                onChange={(e) => setFormData({...formData, location_type: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="in_town">🏠 In Town</option>
                <option value="out_of_town">🚛 Out of Town</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Priority</label>
              <select
                value={formData.priority_level}
                onChange={(e) => setFormData({...formData, priority_level: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="Low">🟢 Low</option>
                <option value="Normal">🟡 Normal</option>
                <option value="High">🟠 High</option>
                <option value="Urgent">🔴 Urgent</option>
              </select>
            </div>
          </div>

          {/* Job Details */}
          <textarea
            placeholder="Job Summary"
            value={formData.job_summary}
            onChange={(e) => setFormData({...formData, job_summary: e.target.value})}
            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 h-20"
          />

          <textarea
            placeholder="Detailed Job Description"
            value={formData.job_details}
            onChange={(e) => setFormData({...formData, job_details: e.target.value})}
            className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 h-24"
            required
          />

          {/* Financial Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="number"
              placeholder="Estimated Cost ($)"
              value={formData.estimated_cost}
              onChange={(e) => setFormData({...formData, estimated_cost: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              step="0.01"
              min="0"
            />
            <select
              value={formData.billing_status}
              onChange={(e) => setFormData({...formData, billing_status: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            >
              <option value="not_billed">💸 Not Billed</option>
              <option value="invoiced">📋 Invoiced</option>
              <option value="paid">✅ Paid</option>
            </select>
          </div>

          <div className="flex gap-4 pt-4">
            <button 
              type="submit" 
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white"
            >
              {loading ? 'Creating...' : 'Create Job'}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white"
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
  );
};

// Job Details Modal
const JobDetailsModal = ({ job, employees, onClose, onUpdate, onEditJob, currentUser }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    start_date: job.start_date || '',
    end_date: job.end_date || '',
    location_type: job.location_type || 'in_town',
    calendar_color: job.calendar_color || ''
  });

  const handleScheduleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/jobs/${job.id}/schedule`, formData);
      alert('Job schedule updated successfully!');
      onUpdate();
    } catch (error) {
      console.error('Error updating job schedule:', error);
      alert('Error updating job schedule. Please try again.');
    }
  };

  const getAssignedEmployeeNames = () => {
    return job.assigned_crew_members
      .map(id => {
        const emp = employees.find(e => e.id === id);
        return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
      })
      .join(', ');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-8 max-w-3xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">📋 Job Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
        </div>
        
        {/* Job Info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          {currentUser?.permissions?.canEdit && onEditJob && (
            <div className="mb-6 flex justify-end">
              <button
                onClick={onEditJob}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2"
              >
                ✏️ Edit Job
              </button>
            </div>
          )}

          <h3 className="text-xl font-bold text-gray-800 mb-2">{job.customer_name}</h3>
          <p className="text-gray-600 mb-1">📍 {job.job_location}</p>
          <p className="text-gray-600 mb-1">📧 {job.contact_email} | 📞 {job.contact_phone}</p>
          <div className="mt-3">
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
              job.location_type === 'out_of_town' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {job.location_type === 'out_of_town' ? '🚛 Out of Town' : '🏠 In Town'}
            </span>
          </div>
        </div>

        {/* Job Description */}
        {job.job_details && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-lg font-bold text-gray-800 mb-2">📝 Job Details</h4>
            <p className="text-gray-700">{job.job_details}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Crew Assignment Modal
const CrewAssignmentModal = ({ job, employees, onClose, onUpdate }) => {
  const [selectedCrew, setSelectedCrew] = useState(job.assigned_crew_members || []);
  const [loading, setLoading] = useState(false);

  const handleCrewToggle = (employeeId) => {
    console.log(`👤 [CREW TOGGLE] Employee ${employeeId}, current crew:`, selectedCrew);
    
    let newCrew;
    if (selectedCrew.includes(employeeId)) {
      newCrew = selectedCrew.filter(id => id !== employeeId);
      console.log(`➖ [CREW TOGGLE] Removing employee ${employeeId}`);
    } else {
      newCrew = [...selectedCrew, employeeId];
      console.log(`➕ [CREW TOGGLE] Adding employee ${employeeId}`);
    }
    
    console.log(`🔄 [CREW TOGGLE] New crew assignment:`, newCrew);
    setSelectedCrew(newCrew);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      console.log(`📝 [CREW ASSIGNMENT] Saving crew for job ${job.id}:`, selectedCrew);
      
      const response = await axios.put(`${API}/jobs/${job.id}/crew`, {
        assigned_crew_members: selectedCrew
      });
      
      console.log(`✅ [CREW ASSIGNMENT] SUCCESS:`, response.data);
      alert('Crew assignment updated successfully!');
      onUpdate();
      onClose(); // Close the modal after successful save
    } catch (error) {
      console.error(`❌ [CREW ASSIGNMENT] ERROR:`, error);
      console.error(`❌ [CREW ASSIGNMENT] ERROR Response:`, error.response?.data);
      
      alert(`Error updating crew assignment: ${error.response?.data?.detail || error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">👥 Assign Crew</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
        </div>
        
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-bold text-blue-800 mb-1">{job.customer_name}</h3>
          <p className="text-blue-600 text-sm">
            {job.start_date && job.end_date 
              ? `${new Date(job.start_date).toLocaleDateString()} - ${new Date(job.end_date).toLocaleDateString()}`
              : 'No dates set'
            }
          </p>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {employees.map((employee) => {
            const isSelected = selectedCrew.includes(employee.id);
            
            return (
              <div
                key={employee.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                }`}
                onClick={() => handleCrewToggle(employee.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-gray-800">
                      {employee.first_name} {employee.last_name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {employee.position} - {employee.department}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'
                    }`}>
                      {isSelected && '✓'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex gap-4">
          <button 
            onClick={handleSave}
            disabled={loading}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white"
          >
            {loading ? 'Saving...' : `Assign ${selectedCrew.length} Crew Member${selectedCrew.length !== 1 ? 's' : ''}`}
          </button>
          <button 
            onClick={onClose}
            className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white"
          >
            Cancel
          </button>
        </div>
        </div>
      </div>
  );
};

// Push Pier Log Fillable Form Component

// NewEventModal
const NewEventModal = ({ employees, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'other',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    all_day: true,
    assigned_employees: [],
    location: '',
    color: '#3B82F6'
  });
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    { value: 'job', label: '💼 Job', color: '#3B82F6' },
    { value: 'vacation', label: '🏖️ Vacation', color: '#10B981' },
    { value: 'meeting', label: '📅 Meeting', color: '#8B5CF6' },
    { value: 'maintenance', label: '🔧 Maintenance', color: '#F59E0B' },
    { value: 'other', label: '📌 Other', color: '#6B7280' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error creating event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      assigned_employees: prev.assigned_employees.includes(employeeId)
        ? prev.assigned_employees.filter(id => id !== employeeId)
        : [...prev.assigned_employees, employeeId]
    }));
  };

  const handleEventTypeChange = (eventType) => {
    const selectedType = eventTypes.find(type => type.value === eventType);
    setFormData(prev => ({
      ...prev,
      event_type: eventType,
      color: selectedType ? selectedType.color : '#3B82F6'
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-800">📅 Create New Event</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Event Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter event title"
              required
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Type *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {eventTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleEventTypeChange(type.value)}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    formData.event_type === type.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="all_day"
              checked={formData.all_day}
              onChange={(e) => setFormData(prev => ({ ...prev, all_day: e.target.checked }))}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="all_day" className="text-sm font-medium text-gray-700">
              All Day Event
            </label>
          </div>

          {/* Time Fields (only show if not all day) */}
          {!formData.all_day && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional event description"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Event location (optional)"
            />
          </div>

          {/* Assigned Employees */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign Employees (Optional)
            </label>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
              {employees.map((employee) => {
                const isSelected = formData.assigned_employees.includes(employee.id);
                return (
                  <div
                    key={employee.id}
                    className={`p-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleEmployeeToggle(employee.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-800">
                          {employee.first_name} {employee.last_name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {employee.position}
                        </p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300'
                      }`}>
                        {isSelected && '✓'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {formData.assigned_employees.length > 0 && (
              <p className="text-sm text-blue-600 mt-2">
                {formData.assigned_employees.length} employee{formData.assigned_employees.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-4 border-t border-gray-200">

            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-bold"
            >
              {loading ? 'Creating...' : '✅ Create Event'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-bold"
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
  );
};

// Customer Care - Phone Intake Archive component

// EventDetailsModal
const EventDetailsModal = ({ event, employees, onClose, onEdit, onDelete, currentUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: event.title || '',
    description: event.description || '',
    event_type: event.event_type || 'other',
    start_date: event.start_date || '',
    end_date: event.end_date || '',
    start_time: event.start_time || '',
    end_time: event.end_time || '',
    all_day: event.all_day || true,
    assigned_employees: event.assigned_employees || [],
    location: event.location || '',
    color: event.color || '#3B82F6'
  });
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    { value: 'job', label: '💼 Job', color: '#3B82F6' },
    { value: 'vacation', label: '🏖️ Vacation', color: '#10B981' },
    { value: 'meeting', label: '📅 Meeting', color: '#8B5CF6' },
    { value: 'maintenance', label: '🔧 Maintenance', color: '#F59E0B' },
    { value: 'other', label: '📌 Other', color: '#6B7280' }
  ];

  const handleEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onEdit(event.id, formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error editing event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    onDelete(event.id);
  };

  const handleEventTypeChange = (eventType) => {
    const selectedType = eventTypes.find(type => type.value === eventType);
    setFormData(prev => ({
      ...prev,
      event_type: eventType,
      color: selectedType ? selectedType.color : '#3B82F6'
    }));
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      assigned_employees: prev.assigned_employees.includes(employeeId)
        ? prev.assigned_employees.filter(id => id !== employeeId)
        : [...prev.assigned_employees, employeeId]
    }));
  };

  const getEventTypeDetails = (type) => {
    return eventTypes.find(t => t.value === type) || eventTypes.find(t => t.value === 'other');
  };

  const getAssignedEmployeeNames = () => {
    return event.assigned_employees
      ?.map(id => {
        const employee = employees.find(emp => emp.id === id);
        return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown Employee';
      })
      .join(', ') || 'No employees assigned';
  };

  if (isEditing) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 rounded-t-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">✏️ Edit Event</h2>
              <button
                onClick={() => setIsEditing(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
          </div>

          <form onSubmit={handleEdit} className="p-6 space-y-6">
            {/* Event Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter event title"
                required
              />
            </div>

            {/* Event Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Type *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {eventTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleEventTypeChange(type.value)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      formData.event_type === type.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            {/* All Day Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="edit_all_day"
                checked={formData.all_day}
                onChange={(e) => setFormData(prev => ({ ...prev, all_day: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="edit_all_day" className="text-sm font-medium text-gray-700">
                All Day Event
              </label>
            </div>

            {/* Time Fields */}
            {!formData.all_day && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Event description"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Event location"
              />
            </div>

            {/* Assigned Employees */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign Employees
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                {employees.map((employee) => {
                  const isSelected = formData.assigned_employees.includes(employee.id);
                  return (
                    <div
                      key={employee.id}
                      className={`p-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleEmployeeToggle(employee.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-800">
                            {employee.first_name} {employee.last_name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {employee.position}
                          </p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300'
                        }`}>
                          {isSelected && '✓'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-4 pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-bold"
              >
                {loading ? 'Saving...' : '💾 Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-bold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // View Mode
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">
                {getEventTypeDetails(event.event_type).label.split(' ')[0]}
              </span>
              <h2 className="text-2xl font-bold text-gray-800">{event.title}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">📅 Event Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Type:</span>
                  <p className="text-gray-800">{getEventTypeDetails(event.event_type).label}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Date:</span>
                  <p className="text-gray-800">
                    {event.start_date === event.end_date 
                      ? new Date(event.start_date).toLocaleDateString()
                      : `${new Date(event.start_date).toLocaleDateString()} - ${new Date(event.end_date).toLocaleDateString()}`
                    }
                  </p>
                </div>
                {!event.all_day && (event.start_time || event.end_time) && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Time:</span>
                    <p className="text-gray-800">
                      {event.start_time && event.end_time 
                        ? `${event.start_time} - ${event.end_time}`
                        : event.start_time || event.end_time
                      }
                    </p>
                  </div>
                )}
                {event.location && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Location:</span>
                    <p className="text-gray-800">{event.location}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">👥 Assigned Staff</h3>
              <div className="space-y-2">
                {event.assigned_employees && event.assigned_employees.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {event.assigned_employees.map((employeeId) => {
                      const employee = employees.find(emp => emp.id === employeeId);
                      return employee ? (
                        <span
                          key={employeeId}
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {employee.first_name} {employee.last_name}
                        </span>
                      ) : (
                        <span
                          key={employeeId}
                          className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          Unknown Employee
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No employees assigned</p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">📝 Description</h3>
              <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{event.description}</p>
            </div>
          )}

          {/* Event Metadata */}
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
              <div>
                <span className="font-medium">Created by:</span> {event.created_by}
              </div>
              <div>
                <span className="font-medium">Created:</span> {new Date(event.created_date).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {currentUser?.rawRole === 'admin' && (
            <div className="flex gap-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => setIsEditing(true)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2"
              >
                ✏️ Edit Event
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2"
              >
                🗑️ Delete Event
              </button>
            </div>
          )}

          {/* Close Button for Non-Admin */}
          {currentUser?.rawRole !== 'admin' && (
            <div className="flex justify-center pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="bg-gray-500 hover:bg-gray-600 text-white py-3 px-8 rounded-lg font-bold"
              >
                Close
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
  );
};

// Safety Module Component

export { CalendarPreviewComponent, CalendarModule, NewEventModal, EventDetailsModal };
export default CalendarModule;
